import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/bi/EmptyState";
import { Loading } from "@/components/bi/Loading";
import { RefreshCcw, Bug, ShieldCheck } from "lucide-react";

const STAGE_COLOR = {
  widget_render: "bg-[color:rgba(232,90,90,0.12)] text-[color:var(--danger)]",
  dashboard_render: "bg-[color:rgba(232,90,90,0.2)] text-[color:var(--danger)]",
  widget_query: "bg-[color:rgba(240,165,0,0.14)] text-amber-600",
  chat_apply: "bg-[color:rgba(43,78,255,0.1)] text-[color:var(--brand-ocean)]",
};

export function AdminDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/diagnostics/render-failures").then(({ data: d }) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading && !data) return <Loading label="Loading diagnostics" />;
  const items = data?.items || [];
  const summary = data?.summary || [];

  return (
    <div data-testid="admin-diagnostics" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-base text-[color:var(--ink)] flex items-center gap-1.5"><Bug className="w-4 h-4 text-[color:var(--danger)]" />Rendering diagnostics</h3>
          <p className="text-xs text-[color:var(--ink-3)]">Client-side widget & dashboard failures reported in the last 14 days.</p>
        </div>
        <Button data-testid="diagnostics-refresh" size="sm" variant="outline" onClick={load} className="bg-[color:var(--surface)]"><RefreshCcw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
      </div>

      {summary.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summary.slice(0, 8).map((s, i) => (
            <span key={i} data-testid="diagnostics-summary-chip" className="text-[11px] px-2.5 py-1 rounded-full border border-[color:var(--glass-border)] bg-[color:var(--surface)] text-[color:var(--ink-2)]">
              <b>{s.count}×</b> {s.stage} · {s.dashboard_id ? `${s.dashboard_id.slice(0, 8)}…` : "—"}
            </span>
          ))}
        </div>
      )}

      {!items.length ? (
        <EmptyState icon={ShieldCheck} title="No rendering failures" description="No client errors have been reported. Dashboards are healthy." />
      ) : (
        <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] overflow-auto max-h-[60vh]">
          <table className="text-xs w-full">
            <thead className="sticky top-0 bg-[color:var(--surface-2)] z-10">
              <tr>{["Time", "Stage", "Dashboard", "Widget", "Message", "Retries", "Ref"].map((h) => <th key={h} className="text-left px-3 py-2.5 font-medium text-[color:var(--ink-2)] whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid="diagnostics-row" className="border-t border-[color:var(--glass-border)] hover:bg-[color:var(--glass-bg)] align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-[color:var(--ink-3)]">{(it.created_at || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="px-3 py-2"><Badge className={"text-[10px] border-0 " + (STAGE_COLOR[it.stage] || "bg-[color:var(--surface-2)]")}>{it.stage}</Badge></td>
                  <td className="px-3 py-2 font-mono text-[color:var(--ink-3)]">{it.dashboard_id ? it.dashboard_id.slice(0, 8) : "—"}</td>
                  <td className="px-3 py-2 font-mono text-[color:var(--ink-3)]">{it.widget_id ? `${it.widget_id.slice(0, 8)} (${it.widget_type || "?"})` : "—"}</td>
                  <td className="px-3 py-2 text-[color:var(--ink-2)] max-w-[380px]"><span className="line-clamp-2">{it.message}</span></td>
                  <td className="px-3 py-2 tabular-nums text-[color:var(--ink-3)]">{it.retry_count || 0}</td>
                  <td className="px-3 py-2 font-mono text-[color:var(--ink-3)]">{it.correlation_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
